import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../security/";
// Route components
import ProtectedRoute from "./ProtectedRoute.jsx";
import RoleBasedRoute from "./RoleBasedRoute.jsx";
import { GROUPS, ROUTES } from "./constants.js";

// Layout components
import { MainLayout, PageLoader } from "../components/index.js";

// Lazy-loaded page components
const Dashboard = React.lazy(() => import("../pages/Dashboard"));
const Profile = React.lazy(() => import("../pages/Profile"));
const Admin = React.lazy(() => import("../pages/Admin"));
const LoginPage = React.lazy(() => import("../pages/LoginPage"));
const ForgotPassword = React.lazy(() => import("../pages/ForgotPassword/"));
const ResetPassword = React.lazy(() => import("../pages/ResetPassword/"));
const SetPassword = React.lazy(() => import("../pages/SetPassword"));
const Users = React.lazy(() => import("../pages/Users/"));
const Projects = React.lazy(() => import("../pages/Projects"));
const ProjectDetail = React.lazy(
  () => import("../pages/Projects/ProjectDetail.jsx")
);

const UnauthorizedAccess = React.lazy(
  () => import("../components/ui/UnauthorizedAccess.jsx")
);
const PageNotFound = React.lazy(() => import("../pages/NotFound"));

// Route configuration
const routeConfig = [
  { path: "/login", element: <LoginPage />, isPublic: true },
  { path: "/password/forgot", element: <ForgotPassword />, isPublic: true },
  {
    path: "/password/reset/confirm",
    element: <ResetPassword />,
    isPublic: true,
  },
  {
    path: "/password/set",
    element: <SetPassword />,
    isPublic: true,
  },

  {
    path: "/dashboard",
    element: <Dashboard />,
    requiresAuth: true,
  },
  {
    path: "/profile",
    element: <Profile />,
    requiresAuth: true,
  },

  {
    path: "/admin",
    element: <Admin />,
    requiresAuth: true,
    requiredGroups: [GROUPS.ADMINS],
  },
  {
    path: "/users",
    element: <Users />,
    requiresAuth: true,
    requiredGroups: [GROUPS.ADMINS],
  },
  {
    path: "/admin/audit",
    element: <div>Audit UI coming in Sprint 1!</div>,
    requiresAuth: true,
    requiredGroups: [GROUPS.ADMINS],
  },

  {
    path: "/projects",
    element: <Projects />,
    requiresAuth: true,
  },
  {
    path: "/projects/:projectId/:tab?",
    element: <ProjectDetail />,
    requiresAuth: true,
  },
  {
    path: "/projects/:projectId/outcomes/:outcomeId",
    element: <ProjectDetail />,
    requiresAuth: true,
  },
  {
    path: "/projects/:projectId/outputs/:outputId",
    element: <ProjectDetail />,
    requiresAuth: true,
  },
  {
    path: "/projects/:projectId/activities/:activityId",
    element: <ProjectDetail />,
    requiresAuth: true,
  },
  {
    path: "/projects/:projectId/indicators/:indicatorId",
    element: <ProjectDetail />,
    requiresAuth: true,
  },

  { path: "/unauthorized", element: <UnauthorizedAccess />, isPublic: true },
  { path: "/404", element: <PageNotFound />, isPublic: true },
];

const AppRouter = () => {
  const { t, ready } = useTranslation("layoutTranslation");
  const { isAuthenticated } = useAuth();
  const isAuthResolved = typeof isAuthenticated === "boolean";
  const suspenseFallback = (
    <PageLoader
      message={
        ready ? t("common.loadingPage", "Loading page...") : "Loading page..."
      }
    />
  );

  const withSuspense = (node, fallback = suspenseFallback) => (
    <React.Suspense fallback={fallback}>{node}</React.Suspense>
  );

  const renderRouteElement = (route) => {
    const {
      requiresAuth,
      requiredPermissions = [],
      requiredGroups = [],
      requireAll = false,
      isPublic,
    } = route;

    // Public routes: simple Suspense (no layout for public pages)
    if (isPublic) {
      return withSuspense(route.element);
    }

    // 1) create the lazy node, but use `null` as Suspense fallback because the guard will show loader
    let node = withSuspense(route.element, null);

    // 2) apply role checks first (wrap node in MainLayout so the layout is gated by the role guard)
    if (requiredPermissions.length > 0 || requiredGroups.length > 0) {
      node = (
        <RoleBasedRoute
          requiredPermissions={requiredPermissions}
          requiredGroups={requiredGroups}
          requireAll={requireAll}
        >
          <MainLayout>{node}</MainLayout>
        </RoleBasedRoute>
      );
    } else if (requiresAuth) {
      // 3) wrap with ProtectedRoute which now controls whether MainLayout is ever rendered
      node = <ProtectedRoute>{<MainLayout>{node}</MainLayout>}</ProtectedRoute>;
    } else {
      // Not public but also not protected -> still wrap in layout
      node = <MainLayout>{node}</MainLayout>;
    }

    return node;
  };

  // Root redirect: wait until we know auth, otherwise show loader
  const rootElement = !isAuthResolved ? (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <PageLoader
        message={
          ready ? t("common.loadingPage", "Loading page...") : "Loading page..."
        }
      />
    </div>
  ) : (
    <Navigate to={isAuthenticated ? ROUTES.PROJECTS : ROUTES.LOGIN} replace />
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={rootElement} />
        {routeConfig.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={renderRouteElement(route)}
          />
        ))}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
